import { useEffect, useState } from "react";
import { CircleMarker, MapContainer, TileLayer, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2 } from "lucide-react";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { useApi } from "@/lib/api";
import { fmtKwp } from "@/lib/apolloCalc";

// Colore dal verde (poche richieste) al rosso (zona più calda)
const heatColor = (ratio) => (ratio > 0.75 ? "#DC2626" : ratio > 0.5 ? "#F97316" : ratio > 0.25 ? "#F59E0B" : "#F5B700");

export default function HeatMap() {
  const api = useApi();
  const [data, setData] = useState(null);
  useEffect(() => {
    api("get", "/admin/heatmap").then(setData);
  }, [api]);
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-slate-400" />;
  const max = Math.max(1, ...data.points.map((p) => p.count));
  const maxRegion = Math.max(1, ...data.regions.map((r) => r.count));
  return (
    <div data-testid="admin-heatmap-page">
      <PageHeader title="Mappa di calore" subtitle="Dove arrivano le richieste dal calcolatore Apollo.AI" />
      <div className="grid gap-6 lg:grid-cols-3">
        <div data-testid="admin-heatmap-container" className="relative z-0 h-[480px] overflow-hidden rounded-2xl border border-slate-200 lg:col-span-2 lg:h-[620px]">
          <MapContainer center={[42.3, 12.6]} zoom={6} minZoom={5} scrollWheelZoom className="h-full w-full">
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {data.points.map((p) => {
              const ratio = p.count / max;
              return (
                <CircleMarker key={`${p.lat}-${p.lng}`} center={[p.lat, p.lng]} radius={10 + ratio * 26}
                  pathOptions={{ color: heatColor(ratio), fillColor: heatColor(ratio), fillOpacity: 0.45, weight: 1.5 }}>
                  <Tooltip>
                    <strong>{p.city}</strong> ({p.region})<br />{p.count} lead · {fmtKwp(p.kwp)} kWp totali
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
          <div className="absolute bottom-3 left-3 z-[400] flex gap-3 rounded-xl bg-white/90 px-3 py-2 text-xs shadow backdrop-blur">
            {[["#F5B700", "Bassa"], ["#F59E0B", "Media"], ["#F97316", "Alta"], ["#DC2626", "Massima"]].map(([c, l]) => (
              <span key={l} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}</span>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <p className="text-sm font-semibold text-slate-900">Classifica regioni</p>
          {data.regions.length === 0 && <p data-testid="heatmap-empty" className="mt-4 text-sm text-slate-500">Nessun lead localizzato ancora.</p>}
          <ul className="mt-4 space-y-3">
            {data.regions.map((r) => (
              <li key={r.region} data-testid={`heatmap-region-${r.region.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                <div className="flex justify-between text-sm"><span>{r.region}</span><span className="font-semibold">{r.count} · {fmtKwp(r.kwp)} kWp</span></div>
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${(r.count / maxRegion) * 100}%`, background: heatColor(r.count / maxRegion) }} /></div>
              </li>
            ))}
          </ul>
          {data.unlocated > 0 && <p className="mt-6 text-xs text-slate-500">{data.unlocated} lead con città non riconosciuta (non mostrati sulla mappa).</p>}
        </div>
      </div>
    </div>
  );
}
